import { useState } from "react"
import { Box, CircularProgress, IconButton, List, ListItem, Tooltip, Typography } from "@mui/material"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendar, faCircleInfo, faPlus, faTrashCan } from "@fortawesome/pro-light-svg-icons"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveCompanyId } from "../../../../../context/contextSelectors"
import {
    CompanyAdminWorkgroupsQuery,
    WorkgroupsForCompanyResponse,
    fetchWorkgroupsForCompany,
} from "../../../../../company/admin/organization/workgroups/workgroupsApi"
import { CreateEditTenantUserFormValues } from "../tenantUserTypes"

const useStyles = makeStyles()((theme) => ({
    listItem: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: ".5rem",
        padding: ".5rem 0",
    },
    listItemText: {
        color: theme.palette.tailwind.slate[700],
        wordBreak: "break-word",
    },
    trashButton: {
        color: "rgba(0, 0, 0, 0.56)",
        fontSize: "1rem",
    },
    empty: {
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[400],
        padding: ".5rem 0",
    },
    addButtonRow: {
        display: "flex",
        justifyContent: "flex-end",
    },
    addButton: {
        "&.MuiButton-root": {
            color: "#2196F3",
            borderColor: "#2196F3",
            padding: "0 .75rem",
            borderRadius: ".5rem",
            "& .MuiButton-startIcon svg": {
                width: ".8125rem",
                height: ".75rem",
            },
        },
    },
    dialogLoading: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "6rem",
    },
    dialogList: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
        padding: "0 1.5rem 1.5rem",
    },
    pickerItem: {
        display: "flex",
        flexDirection: "column",
        gap: ".375rem",
        width: "100%",
        padding: ".75rem 1rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".5rem",
        cursor: "pointer",
        transition: "background-color .15s ease, border-color .15s ease",
        "&:hover": {
            backgroundColor: theme.palette.tailwind.slate[50],
            borderColor: "#2196F3",
        },
        "&:focus-visible": {
            outline: `2px solid #2196F3`,
            outlineOffset: "2px",
        },
    },
    pickerHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: ".5rem",
    },
    pickerName: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[900],
    },
    infoIcon: {
        display: "inline-flex",
        color: theme.palette.tailwind.slate[400],
        fontSize: ".875rem",
        cursor: "pointer",
    },
    pickerCalendarRow: {
        display: "flex",
        alignItems: "center",
        gap: ".375rem",
        color: theme.palette.tailwind.slate[500],
    },
    pickerCalendarIcon: {
        fontSize: ".75rem",
    },
    pickerDescription: {
        color: theme.palette.tailwind.slate[500],
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
    },
    dialogEmpty: {
        textAlign: "center",
        color: theme.palette.tailwind.slate[400],
        padding: "1rem 2.5rem",
    },
}))

const selectWorkgroups = (data: WorkgroupsForCompanyResponse) => data?.workgroups || []

export function WorkgroupsMembershipPanel() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const companyId = useAppSelector(selectEffectiveCompanyId)
    const { values, setFieldValue } = useFormikContext<CreateEditTenantUserFormValues>()

    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)

    const currentWorkgroups = values.workgroups

    const workgroupsResult = useApiQuery(fetchWorkgroupsForCompany, {
        queryName: CompanyAdminWorkgroupsQuery.LIST,
        dependencies: {
            companyId: companyId ?? 0,
        },
        enabled: isAddDialogOpen && Boolean(companyId),
        select: selectWorkgroups,
    })

    const availableWorkgroups = (workgroupsResult.data ?? []).filter(
        (workgroup) => !currentWorkgroups.some((current) => current.id === workgroup.id),
    )

    const handleAdd = (workgroup: { id: number; name: string }) => {
        setFieldValue("workgroups", [...currentWorkgroups, { id: workgroup.id, name: workgroup.name }])
        setIsAddDialogOpen(false)
    }

    const handleRemove = (workgroupId: number) => {
        setFieldValue(
            "workgroups",
            currentWorkgroups.filter((workgroup) => workgroup.id !== workgroupId),
        )
    }

    return (
        <>
            {currentWorkgroups.length > 0 ? (
                <List disablePadding>
                    {currentWorkgroups.map((workgroup) => (
                        <ListItem key={workgroup.id} className={classes.listItem} disableGutters>
                            <Typography variant="body2" className={classes.listItemText}>
                                {workgroup.name}
                            </Typography>
                            <IconButton
                                aria-label={l10n.getString("company-user-workgroups-panel-remove")}
                                onClick={() => handleRemove(workgroup.id)}
                            >
                                <FontAwesomeIcon icon={faTrashCan} className={classes.trashButton} />
                            </IconButton>
                        </ListItem>
                    ))}
                </List>
            ) : (
                <LocalizedStrict id="company-user-workgroups-panel-empty">
                    <Typography variant="body2" className={classes.empty}>
                        This user is not a member of any workgroups yet.
                    </Typography>
                </LocalizedStrict>
            )}

            <Box className={classes.addButtonRow}>
                <LocalizedStrict id="company-user-workgroups-panel-add-button">
                    <StandardButton
                        variant="outlined"
                        fullWidth={false}
                        className={classes.addButton}
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => setIsAddDialogOpen(true)}
                    >
                        Add
                    </StandardButton>
                </LocalizedStrict>
            </Box>

            {isAddDialogOpen && (
                <StandardDialog
                    dialogTitle={l10n.getString("company-user-workgroups-panel-add-dialog-title")}
                    open={isAddDialogOpen}
                    onClose={() => setIsAddDialogOpen(false)}
                    maxWidthProp="sm"
                    scroll="body"
                >
                    {workgroupsResult.isInitialLoading ? (
                        <Box className={classes.dialogLoading}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : availableWorkgroups.length > 0 ? (
                        <Box className={classes.dialogList}>
                            {availableWorkgroups.map((workgroup) => (
                                <Box
                                    key={workgroup.id}
                                    role="button"
                                    tabIndex={0}
                                    className={classes.pickerItem}
                                    onClick={() => handleAdd(workgroup)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault()
                                            handleAdd(workgroup)
                                        }
                                    }}
                                >
                                    <Box className={classes.pickerHeader}>
                                        <Typography variant="body2" className={classes.pickerName}>
                                            {workgroup.name}
                                        </Typography>
                                        {workgroup.description && (
                                            <Tooltip title={workgroup.description} arrow>
                                                <Box component="span" className={classes.infoIcon}>
                                                    <FontAwesomeIcon icon={faCircleInfo} />
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Box>
                                    {workgroup.calendarEmailAddress && (
                                        <Box className={classes.pickerCalendarRow}>
                                            <FontAwesomeIcon icon={faCalendar} className={classes.pickerCalendarIcon} />
                                            <Typography variant="caption">{workgroup.calendarEmailAddress}</Typography>
                                        </Box>
                                    )}
                                    {workgroup.description && (
                                        <Typography variant="body2" className={classes.pickerDescription}>
                                            {workgroup.description}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <LocalizedStrict id="company-user-workgroups-panel-add-dialog-empty">
                            <Typography variant="body1" className={classes.dialogEmpty}>
                                There are no more workgroups to add.
                            </Typography>
                        </LocalizedStrict>
                    )}
                </StandardDialog>
            )}
        </>
    )
}
